/**
 * Per-turn "Download" dropdown that lives in the chat answer footer.
 *
 * Replaces the earlier single-icon `DownloadMarkdownButton`. Always offers
 * Markdown (.md), Word (.docx), and PowerPoint (.pptx); additionally
 * surfaces Excel (.xlsx) and CSV items when the answer body contains at
 * least one GFM table — the spreadsheet exports are *only* for the tables
 * inside the answer, not the Q&A itself. When the answer has multiple
 * tables, the spreadsheet items fan out into a sub-list.
 *
 * Markdown + spreadsheet exports run fully client-side. Word and PowerPoint
 * go through `POST /api/v1/agent/export/{docx,pptx}` because faithful
 * markdown→Office rendering (branded headings, citation footer with
 * hyperlinks, GFM tables, code blocks) is much easier server-side with the
 * `docx` / `pptxgenjs` libraries than in a browser.
 */

import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import * as XLSX from 'xlsx';
import type { AgentCitation } from '@kennisbank/shared';
import { DownloadIcon, FileTextIcon, FileIcon, PresentationIcon, TableIcon } from './icons.js';
import { ApiError } from '../api/client.js';
import {
  extractMarkdownTables,
  sheetNameFor,
  tableToCsv,
  type ExtractedTable,
} from '../lib/markdown-tables.js';

interface DownloadMenuProps {
  question: string;
  answer: string;
  citations: AgentCitation[];
  /** Optional model label (e.g. "Claude Opus 4.7") woven into the docx footer. */
  modelLabel?: string;
}

interface ExportTemplate {
  id: string;
  label: string;
  templateClass: 'letterhead' | 'proposal';
  entity: string | null;
}

export function DownloadMenu({
  question,
  answer,
  citations,
  modelLabel,
}: DownloadMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [docxBusy, setDocxBusy] = useState(false);
  const [docxError, setDocxError] = useState<string | null>(null);
  const [pptxBusy, setPptxBusy] = useState(false);
  const [pptxError, setPptxError] = useState<string | null>(null);
  const [letterheadBusy, setLetterheadBusy] = useState(false);
  const [letterheadError, setLetterheadError] = useState<string | null>(null);
  const [proposalBusy, setProposalBusy] = useState(false);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<ExportTemplate[] | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const tables = useMemo(() => extractMarkdownTables(answer), [answer]);
  const baseFilename = useMemo(() => slugifyForFilename(question), [question]);

  // Fetch the letterhead/proposal templates the user can pick, once, when the
  // menu first opens. Failure is silent — the items fall back to their default.
  useEffect(() => {
    if (!open || templates !== null) return;
    void fetch('/api/v1/agent/export/templates', {
      credentials: 'include',
      headers: { ...devTokenHeader() },
    })
      .then((r) => (r.ok ? r.json() : { templates: [] }))
      .then((d: { templates?: ExportTemplate[] }) => setTemplates(d.templates ?? []))
      .catch(() => setTemplates([]));
  }, [open, templates]);

  const letterheads = (templates ?? []).filter((t) => t.templateClass === 'letterhead');
  const proposals = (templates ?? []).filter((t) => t.templateClass === 'proposal');
  const pick = (list: ExportTemplate[], defaultId: string): ExportTemplate[] => {
    const def = list.find((t) => t.id === defaultId) ?? list[0];
    return def ? [def, ...list.filter((t) => t.id !== def.id)] : [];
  };
  const letterheadChoices = pick(letterheads, '1nebula-letterhead');
  const proposalChoices = pick(proposals, 'oneview-proposal');

  // Outside-click + Escape close. Mounted only while open so we don't pay
  // the listener cost for every collapsed menu in a long chat.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent): void => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const downloadMarkdown = (): void => {
    const md = answerToMarkdownString({ question, answer, citations });
    downloadBlob(`${baseFilename}.md`, new Blob([md], { type: 'text/markdown;charset=utf-8' }));
    setOpen(false);
  };

  const downloadServerExport = async (
    format: 'docx' | 'pptx',
    setBusy: (v: boolean) => void,
    setError: (v: string | null) => void,
    fallbackMessage: string,
  ): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/agent/export/${format}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...devTokenHeader() },
        body: JSON.stringify({
          question,
          answer,
          citations,
          ...(modelLabel ? { modelLabel } : {}),
        }),
      });
      if (!res.ok) {
        let detail: { error?: string; message?: string } | null = null;
        try {
          detail = (await res.json()) as { error?: string; message?: string };
        } catch {
          /* binary or empty body — ignore */
        }
        throw new ApiError(
          res.status,
          detail?.error ?? `http_${res.status}`,
          detail?.message ?? `Export failed (${res.status})`,
        );
      }
      const blob = await res.blob();
      downloadBlob(`${baseFilename}.${format}`, blob);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : fallbackMessage);
    } finally {
      setBusy(false);
    }
  };

  const downloadDocx = (): Promise<void> =>
    downloadServerExport(
      'docx',
      setDocxBusy,
      setDocxError,
      'Could not generate the Word document. Please try again.',
    );

  const downloadPptx = (): Promise<void> =>
    downloadServerExport(
      'pptx',
      setPptxBusy,
      setPptxError,
      'Could not generate the PowerPoint deck. Please try again.',
    );

  // Letterhead = the answer body rendered onto a OneNebula brand letterhead.
  // templateId selects the entity; omitted → server default (1Nebula).
  const downloadLetterhead = async (templateId?: string): Promise<void> => {
    setLetterheadBusy(true);
    setLetterheadError(null);
    try {
      const res = await fetch('/api/v1/agent/export/letterhead', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...devTokenHeader() },
        body: JSON.stringify({
          title: question,
          content: answer,
          ...(templateId ? { templateId } : {}),
        }),
      });
      if (!res.ok) {
        let detail: { error?: string; message?: string } | null = null;
        try {
          detail = (await res.json()) as { error?: string; message?: string };
        } catch {
          /* binary or empty body */
        }
        throw new ApiError(
          res.status,
          detail?.error ?? `http_${res.status}`,
          detail?.message ?? `Letterhead export failed (${res.status})`,
        );
      }
      downloadBlob(`${baseFilename}-letterhead.docx`, await res.blob());
      setOpen(false);
    } catch (err) {
      setLetterheadError(err instanceof Error ? err.message : 'Could not generate the letterhead.');
    } finally {
      setLetterheadBusy(false);
    }
  };

  // Proposal = the agent's draft (this answer) extracted into a tagged proposal
  // template (option A — extraction server-side). templateId selects which.
  const downloadProposal = async (templateId: string): Promise<void> => {
    setProposalBusy(true);
    setProposalError(null);
    try {
      const res = await fetch('/api/v1/agent/export/proposal/from-answer', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...devTokenHeader() },
        body: JSON.stringify({ templateId, answer, title: question }),
      });
      if (!res.ok) {
        let detail: { error?: string; message?: string } | null = null;
        try {
          detail = (await res.json()) as { error?: string; message?: string };
        } catch {
          /* binary or empty body */
        }
        throw new ApiError(
          res.status,
          detail?.error ?? `http_${res.status}`,
          detail?.message ?? `Proposal export failed (${res.status})`,
        );
      }
      downloadBlob(`${baseFilename}-proposal.docx`, await res.blob());
      setOpen(false);
    } catch (err) {
      setProposalError(err instanceof Error ? err.message : 'Could not generate the proposal.');
    } finally {
      setProposalBusy(false);
    }
  };

  const downloadAllTablesXlsx = (): void => {
    if (tables.length === 0) return;
    const wb = XLSX.utils.book_new();
    tables.forEach((t, i) => {
      const ws = XLSX.utils.aoa_to_sheet([t.headers, ...t.rows]);
      XLSX.utils.book_append_sheet(wb, ws, sheetNameFor(t, i));
    });
    XLSX.writeFile(wb, `${baseFilename}-tables.xlsx`);
    setOpen(false);
  };

  const downloadSingleTableXlsx = (t: ExtractedTable, index: number): void => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([t.headers, ...t.rows]);
    XLSX.utils.book_append_sheet(wb, ws, sheetNameFor(t, index));
    const suffix =
      tables.length > 1 ? `-${slugifyForFilename(t.heading) || `table-${index + 1}`}` : '-table';
    XLSX.writeFile(wb, `${baseFilename}${suffix}.xlsx`);
    setOpen(false);
  };

  const downloadSingleTableCsv = (t: ExtractedTable, index: number): void => {
    const csv = tableToCsv(t);
    // BOM keeps Excel-on-Windows happy with UTF-8 contents. Escape-form
    // U+FEFF so eslint's no-irregular-whitespace doesn't trip on the raw
    // BOM character.
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const suffix =
      tables.length > 1 ? `-${slugifyForFilename(t.heading) || `table-${index + 1}`}` : '-table';
    downloadBlob(`${baseFilename}${suffix}.csv`, blob);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Download"
        aria-label="Download"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
          open
            ? 'bg-ink-900/5 text-ink-900'
            : 'text-ink-900/50 hover:bg-ink-900/5 hover:text-ink-900'
        }`}
      >
        <DownloadIcon size={14} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 w-64 overflow-hidden rounded-lg border border-ring2 bg-white shadow-lg ring-1 ring-black/5"
        >
          <MenuItem
            icon={<FileIcon size={14} />}
            label="Markdown (.md)"
            description="Plain text with citation list"
            onClick={downloadMarkdown}
          />
          <MenuItem
            icon={<FileTextIcon size={14} />}
            label={docxBusy ? 'Preparing Word…' : 'Word (.docx)'}
            description="Branded document with sources"
            onClick={() => {
              if (!docxBusy) void downloadDocx();
            }}
            disabled={docxBusy}
          />
          <MenuItem
            icon={<PresentationIcon size={14} />}
            label={pptxBusy ? 'Preparing PowerPoint…' : 'PowerPoint (.pptx)'}
            description="Branded slide deck with sources"
            onClick={() => {
              if (!pptxBusy) void downloadPptx();
            }}
            disabled={pptxBusy}
          />
          {/* Letterhead — first is the default; the rest are other entities. */}
          {(letterheadChoices.length ? letterheadChoices : [null]).map((t, idx) => (
            <MenuItem
              key={t?.id ?? 'letterhead-default'}
              icon={<FileTextIcon size={14} />}
              label={
                letterheadBusy
                  ? 'Preparing letterhead…'
                  : idx === 0
                    ? 'Word (letterhead)'
                    : `Letterhead — ${t?.entity ?? t?.label}`
              }
              description={
                idx === 0
                  ? `On the ${t?.entity ?? 'OneNebula'} letterhead`
                  : `Word on this letterhead`
              }
              onClick={() => {
                if (!letterheadBusy) void downloadLetterhead(t?.id);
              }}
              disabled={letterheadBusy}
            />
          ))}
          {/* Proposal — first is the default (OneView); the rest are alternatives. */}
          {(proposalChoices.length
            ? proposalChoices
            : [{ id: 'oneview-proposal', label: 'OneView proposal' } as ExportTemplate]
          ).map((t, idx) => (
            <MenuItem
              key={t.id}
              icon={<FileTextIcon size={14} />}
              label={
                proposalBusy
                  ? 'Preparing proposal…'
                  : idx === 0
                    ? 'OneView proposal'
                    : `Proposal — ${t.label}`
              }
              description={
                idx === 0 ? 'Fills the OneView proposal template' : 'Fills this proposal template'
              }
              onClick={() => {
                if (!proposalBusy) void downloadProposal(t.id);
              }}
              disabled={proposalBusy}
            />
          ))}

          {tables.length > 0 && (
            <>
              <Divider
                label={
                  tables.length === 1 ? 'Table in answer' : `Tables in answer (${tables.length})`
                }
              />

              {tables.length === 1 && tables[0] && (
                <>
                  <MenuItem
                    icon={<TableIcon size={14} />}
                    label="Excel (.xlsx)"
                    description={tables[0].heading}
                    onClick={() => downloadSingleTableXlsx(tables[0] as ExtractedTable, 0)}
                  />
                  <MenuItem
                    icon={<TableIcon size={14} />}
                    label="CSV (.csv)"
                    description={tables[0].heading}
                    onClick={() => downloadSingleTableCsv(tables[0] as ExtractedTable, 0)}
                  />
                </>
              )}

              {tables.length > 1 && (
                <>
                  <MenuItem
                    icon={<TableIcon size={14} />}
                    label="All tables → Excel"
                    description="One sheet per table"
                    onClick={downloadAllTablesXlsx}
                  />
                  {tables.map((t, i) => (
                    <div
                      key={t.id}
                      className="grid grid-cols-[1fr_auto_auto] items-center gap-2 px-3 py-1.5 hover:bg-nebula-tint/50"
                    >
                      <span className="truncate text-xs text-ink-900/80" title={t.heading}>
                        {i + 1}. {t.heading}
                      </span>
                      <button
                        type="button"
                        onClick={() => downloadSingleTableXlsx(t, i)}
                        className="rounded-md border border-ring2 px-2 py-0.5 text-[10px] font-medium text-ink-900/80 hover:border-nebula-secondary hover:text-nebula-secondary"
                      >
                        xlsx
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadSingleTableCsv(t, i)}
                        className="rounded-md border border-ring2 px-2 py-0.5 text-[10px] font-medium text-ink-900/80 hover:border-nebula-secondary hover:text-nebula-secondary"
                      >
                        csv
                      </button>
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          {docxError && (
            <p
              className="border-t border-ring2 bg-red-50 px-3 py-2 text-[11px] text-red-700"
              role="alert"
            >
              {docxError}
            </p>
          )}
          {letterheadError && (
            <p
              className="border-t border-ring2 bg-red-50 px-3 py-2 text-[11px] text-red-700"
              role="alert"
            >
              {letterheadError}
            </p>
          )}
          {proposalError && (
            <p
              className="border-t border-ring2 bg-red-50 px-3 py-2 text-[11px] text-red-700"
              role="alert"
            >
              {proposalError}
            </p>
          )}
          {pptxError && (
            <p
              className="border-t border-ring2 bg-red-50 px-3 py-2 text-[11px] text-red-700"
              role="alert"
            >
              {pptxError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  description,
  onClick,
  disabled,
}: {
  icon: JSX.Element;
  label: string;
  description?: string;
  onClick: () => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-start gap-2 px-3 py-2 text-left text-xs hover:bg-nebula-tint/50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className="mt-[1px] text-ink-900/60">{icon}</span>
      <span className="flex-1">
        <span className="block font-medium text-ink-900">{label}</span>
        {description && <span className="block text-[10px] text-ink-900/55">{description}</span>}
      </span>
    </button>
  );
}

function Divider({ label }: { label: string }): JSX.Element {
  return (
    <div className="border-t border-ring2 bg-nebula-tint/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink-900/55">
      {label}
    </div>
  );
}

/**
 * Same shape as the legacy `answerToMarkdown` on the page — duplicated here
 * so the DownloadMenu is self-contained. If the page's copy ever moves into
 * a shared lib, prefer importing it back from there.
 */
function answerToMarkdownString({
  question,
  answer,
  citations,
}: {
  question: string;
  answer: string;
  citations: AgentCitation[];
}): string {
  const lines: string[] = [
    '# Knowledge Agent answer',
    '',
    '## Question',
    '',
    question.trim(),
    '',
    '## Answer',
    '',
    answer.trim(),
  ];
  if (citations.length > 0) {
    lines.push('', '## Sources', '');
    citations.forEach((c, i) => {
      const meta = [c.author, c.page != null && c.page > 0 ? `p. ${c.page}` : null]
        .filter(Boolean)
        .join(' · ');
      lines.push(`${i + 1}. **${c.title}** — ${meta || '—'}  \n   \`${c.chunkId}\``);
    });
  }
  lines.push('', `_Generated by OneNebula Knowledge Agent · ${new Date().toISOString()}_`);
  return lines.join('\n');
}

function slugifyForFilename(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'knowledge-agent-answer'
  );
}

function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const DEV_TOKEN_STORAGE_KEY = 'kennisbank.devToken';
function devTokenHeader(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const token = window.sessionStorage.getItem(DEV_TOKEN_STORAGE_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}
